import { useEffect, useMemo, useState } from "react";
import { http, API } from "../api";
import { Users, Search, Phone, MessageCircle, MapPin, Facebook, Instagram, Linkedin } from "lucide-react";
import { PageHeader, EmptyState } from "../shared";

export default function AdminMembers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [roles, setRoles] = useState([]);

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (role) params.set("role", role);
    http.get(`/admin-members${params.toString() ? "?" + params.toString() : ""}`).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, role]);
  useEffect(() => { http.get("/roles").then((r) => setRoles(r.data.roles || [])).catch(() => {}); }, []);

  const byRole = useMemo(() => {
    const map = {};
    rows.forEach((p) => { const r = p.role || "Team Member"; (map[r] = map[r] || []).push(p); });
    return map;
  }, [rows]);

  return (
    <>
      <PageHeader eyebrow="LEADERSHIP DIRECTORY" title="Admin & Members" subtitle={`${rows.length} team members across roles`} />

      <section className="panel">
        <div className="directory-toolbar">
          <div className="inner-search">
            <Search size={17} />
            <input data-testid="am-search-input" placeholder="Search by name…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="filters">
            <select data-testid="am-role-filter" value={role} onChange={(e) => setRole(e.target.value)}>
              <option value="">All roles</option>
              {roles.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        </div>

        {rows.length === 0 ? (
          <EmptyState icon={Users} title="No team members flagged yet" hint="Open a person in People and set 'Show on Admin & Members tab' to Yes." />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 22 }} data-testid="am-groups">
            {Object.keys(byRole).sort().map((r) => (
              <div key={r} data-testid={`am-group-${r.replace(/\s+/g, "-")}`}>
                <h3 style={{ margin: "6px 0 12px", fontSize: 13, letterSpacing: ".3px", textTransform: "uppercase", color: "var(--muted)" }}>{r} · {byRole[r].length}</h3>
                <div className="grid-cards">
                  {byRole[r].map((p, i) => (
                    <MemberCard key={p.contact_id} p={p} testid={`am-card-${r.replace(/\s+/g, "-")}-${i}`} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function MemberCard({ p, testid }) {
  const cover = p.cover_photo ? `${API}/files/${p.cover_photo}` : "";
  const avatar = p.profile_photo ? `${API}/files/${p.profile_photo}` : "";
  const wa = (p.whatsapp || p.mobile || "").replace(/[^0-9]/g, "");
  const initials = p.name.split(" ").map((x) => x[0]).slice(0, 2).join("");
  return (
    <div className="org-card" data-testid={testid} style={{ padding: 0, overflow: "hidden" }}>
      <div style={{
        height: 96,
        background: cover ? `#eee center/cover no-repeat url(${cover})` : "linear-gradient(120deg,#0f766e,#f0bd61)",
      }} data-testid={`${testid}-cover`} />
      <div style={{ padding: "0 16px 16px" }}>
        <div style={{
          width: 76, height: 76, borderRadius: "50%",
          background: avatar ? `#f0f0f0 center/cover no-repeat url(${avatar})` : "linear-gradient(160deg,#c8dfd8,#eecf8e)",
          border: "4px solid white", marginTop: -38, display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 700, color: "#0f766e", fontSize: 22, boxShadow: "0 4px 12px rgba(0,0,0,.08)",
        }} data-testid={`${testid}-avatar`}>{!avatar && initials}</div>
        <div style={{ marginTop: 10 }}>
          <b style={{ fontSize: 15 }}>{p.name}</b>
          <span className="chip green" style={{ marginLeft: 8 }}>{p.role || "Member"}</span>
          <p className="hint" style={{ margin: "4px 0 8px" }}>
            {p.profession || "—"}{p.age ? ` · Age ${p.age}` : ""} · Ward {p.ward} · Booth {p.booth}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--muted)" }}>
            {p.mobile && <span><Phone size={12} /> {p.mobile}</span>}
            {p.home_location && <span><MapPin size={12} /> Home: {p.home_location}</span>}
            {p.current_location && <span><MapPin size={12} /> Now: {p.current_location}</span>}
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
            {wa && (
              <a href={`https://wa.me/${wa.startsWith("91") ? wa : "91" + wa}`} target="_blank" rel="noreferrer" className="link" data-testid={`${testid}-whatsapp`} style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#128C3E" }}>
                <MessageCircle size={13} /> WhatsApp
              </a>
            )}
            {p.facebook_url && <a href={p.facebook_url} target="_blank" rel="noreferrer" className="link" data-testid={`${testid}-facebook`} style={{ color: "#1877F2" }}><Facebook size={13} /></a>}
            {p.instagram_url && <a href={p.instagram_url} target="_blank" rel="noreferrer" className="link" data-testid={`${testid}-instagram`} style={{ color: "#c13584" }}><Instagram size={13} /></a>}
            {p.linkedin_url && <a href={p.linkedin_url} target="_blank" rel="noreferrer" className="link" data-testid={`${testid}-linkedin`} style={{ color: "#0A66C2" }}><Linkedin size={13} /></a>}
          </div>
        </div>
      </div>
    </div>
  );
}
